import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Camera, FileUp, Lock, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import UsageBadge from "@/components/UsageBadge";
import WelcomeBanner from "@/components/WelcomeBanner";
import useUsage from "@/hooks/useUsage";
import { API, LANGUAGES, useApp } from "@/state/AppContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHIPS = ["Fristen", "Beträge", "Aktenzeichen", "Absender", "Handlungen", "Dringlichkeit"];

export default function HomePage() {
  const navigate = useNavigate();
  const { language, setLanguage, user, authHeaders } = useApp();
  const usage = useUsage();
  const blocked = !usage.loading && usage.tier === "free" && (usage.trial_expired || usage.remaining <= 0);
  const [doneCount, setDoneCount] = useState(0);

  useEffect(() => {
    if (!user) { setDoneCount(0); return; }
    axios.get(`${API}/documents`, { headers: authHeaders })
      .then(r => setDoneCount((r.data || []).filter(d => d.status === "done").length))
      .catch(() => {});
  }, [user, authHeaders]);

  const goUpload = (mode) => {
    if (blocked) { navigate("/pricing"); return; }
    navigate(`/upload?mode=${mode}`);
  };

  return (
    <div className="px-5 pt-6 pb-8" data-testid="home-page">
      <WelcomeBanner />
      <div className="flex flex-col items-start gap-4">
        <Logo size="lg" />
        <h1 className="text-3xl leading-tight font-bold text-slate-900">
          Deutsche Briefe verstehen.<br />
          <span className="text-blue-600">In deiner Sprache.</span>
        </h1>
        <p className="text-slate-600 text-[15px] leading-relaxed">
          Fotografiere einen deutschen Brief oder lade ein PDF hoch. BriefAssist zeigt dir,
          was wichtig ist, welche Fristen gelten und was du als Nächstes tun kannst.
        </p>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <button
          onClick={() => goUpload("camera")}
          data-testid="home-camera-btn"
          className={`group flex items-center justify-center gap-3 min-h-[64px] rounded-3xl font-semibold text-[17px] active:scale-[0.98] transition-transform shadow-md ${blocked ? "bg-slate-200 text-slate-500 shadow-none" : "bg-blue-600 text-white shadow-blue-600/20"}`}
        >
          <Camera className="w-6 h-6" /> Brief fotografieren
        </button>
        <button
          onClick={() => goUpload("pdf")}
          data-testid="home-pdf-btn"
          className={`flex items-center justify-center gap-3 min-h-[60px] rounded-3xl font-semibold active:scale-[0.98] transition-transform border ${blocked ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-blue-50 text-blue-700 border-blue-100"}`}
        >
          <FileUp className="w-5 h-5" /> PDF hochladen
        </button>
        {blocked && (
          <button onClick={() => navigate("/pricing")} data-testid="home-upgrade-cta"
            className="mt-1 text-sm font-semibold text-red-700 py-1">
            → Jetzt Plan wählen, um weiterzumachen
          </button>
        )}
      </div>

      <div className="mt-8">
        <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Erklärungssprache</label>
        <div className="mt-2">
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger data-testid="home-language-select" className="h-14 rounded-2xl bg-blue-50 border-blue-100 text-slate-900 font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map(l => (
                <SelectItem key={l.code} value={l.code} data-testid={`lang-opt-${l.code}`}>{l.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-blue-50/60 border border-blue-100 p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 leading-relaxed">
          Deine Dokumente werden vertraulich verarbeitet.
        </p>
      </div>

      <div className="mt-4">
        <UsageBadge />
      </div>

      {user && doneCount > 0 && (
        <button
          onClick={() => navigate("/letters?filter=done")}
          data-testid="done-streak-card"
          className="mt-4 w-full flex items-center gap-3 rounded-2xl bg-gradient-to-r from-emerald-50 to-emerald-100/60 border border-emerald-200 p-3 text-left active:scale-[0.99] transition"
        >
          <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-emerald-900">
              {doneCount} {doneCount === 1 ? "Brief erledigt" : "Briefe erledigt"}
            </div>
            <div className="text-xs text-emerald-800/70">Weiter so — du hältst deine Post im Griff.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-emerald-700 shrink-0" />
        </button>
      )}

      <div className="mt-6">
        <div className="flex items-center gap-2 text-slate-600 text-sm mb-3">
          <Sparkles className="w-4 h-4 text-amber-500" /> BriefAssist erkennt automatisch:
        </div>
        <div className="flex flex-wrap gap-2">
          {CHIPS.map(c => (
            <span key={c} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-[13px] text-slate-700 font-medium">{c}</span>
          ))}
        </div>
      </div>

      <p className="mt-8 text-center text-slate-500 text-xs">
        Verstehen. Übersetzen. Handeln.
      </p>
    </div>
  );
}
